/**
 * Pedagogical helper that maps a vehicle (tier + specialties) to the list of
 * contract types it can carry — and to those it cannot. Used by BuyTab.jsx
 * to clarify "what does Tier X actually unlock?" type questions.
 *
 * Source of truth :
 *  - Tier classifies the size/range envelope (urban → semi-trailer national).
 *  - `specialties` field narrows further (frigo, ADR, animals, BTP, etc.).
 *
 * 31/05/2026 — Tier 4 & 5 made SPECIALTY-DRIVEN (was tier-blanket).
 *   Reason: Tier 5 has 3 very different models (V8 prestige diesel /
 *   porte-engins BTP / electric ZFE) that should NOT share the same
 *   contract eligibility list. The previous tier-blanket approach
 *   wrongly showed "ZFE illimité (électrique)" on the V8 36L/100 diesel
 *   and "Export EU prestige" on a porte-engins with only 350 km/j range.
 *
 * The returned shape is intentionally simple — UI just renders the strings.
 */

const TIER_PROFILE = {
  1: {
    label: 'Fourgon léger (< 3,5 T) — Permis B',
    accessible: ['Messagerie urbaine', 'Colis express', 'Petite distribution'],
    blocked: ['Vrac lourd', 'BTP / Matériaux', 'Longue distance', 'Export UE'],
    hint: 'Idéal pour démarrer en zone urbaine et petite couronne. Pas de gros volumes.',
  },
  2: {
    label: 'Porteur 7-12 T — Permis C',
    accessible: ['Distribution régionale', 'Palettes / vrac moyen', 'Approvisionnement chantier', 'BTP léger'],
    blocked: ['Longue distance', 'Export UE', 'Engins de chantier lourds'],
    hint: 'Polyvalent régional. Idéal entre les zones urbaines et le départemental.',
  },
  3: {
    label: 'Semi-remorque 44 T — Permis CE',
    accessible: ['Contrats nationaux', 'Longue distance', 'Vrac lourd', 'Export UE (avec licence)'],
    blocked: ['Transport frigorifique (sauf Frigo dédié)', 'ADR / matières dangereuses'],
    hint: 'Le cheval de bataille pour le national. Volume × 3 par rapport au porteur.',
  },
  4: {
    label: 'Véhicule spécialisé 44 T — Permis CE',
    // Tier 4 is specialty-driven: each variant (Frigo / ADR) has its own
    // access list derived from `specialties`. The blocked list and hint
    // are common to the tier.
    // 05/02/2026 — Q1 retour user prod : variante Bétaillère retirée.
    specialtyDriven: true,
    blocked: ['Messagerie urbaine (gabarit trop grand)'],
    hint: 'Modèle hautement spécialisé : frigo ou ADR selon configuration. N\'accepte QUE les contrats dédiés à sa spécialité.',
  },
  5: {
    label: 'Prestige / Export / Vert — Permis CE',
    // Tier 5 is also specialty-driven: V8 prestige (Export EU only),
    // Porte-engins (BTP only), Electric (ZFE only). They share gabarit
    // XXL blockers but have OPPOSITE accessible lists.
    specialtyDriven: true,
    blocked: ['Urbain étroit (gabarit XXL)'],
    hint: 'Modèle haut de gamme spécialisé : prestige V8, porte-engins BTP ou 100 % électrique selon configuration.',
  },
};

/**
 * Per-specialty enrichment.
 *
 * `label`   : displayed in the "accessible" list of the BuyTab card.
 * `blocks`  : extra contract types this specialty EXPLICITLY rules out
 *             (e.g. an electric truck blocks BTP lourd).
 */
const SPECIALTY_HINTS = {
  // ── Anciens libellés (compat saves legacy & tests historiques) ──────────
  Frigorifique:           { label: 'Frigo (GMS, restauration, pharma)' },
  Pharma:                 { label: 'Pharma / DPI à température dirigée' },
  GMS:                    { label: 'Tournées GMS (grandes et moyennes surfaces)' },
  ADR:                    { label: 'ADR / matières dangereuses (citerne, chimie)' },
  'Matières dangereuses': { label: 'ADR / pétrole / produits sensibles' },
  Messagerie:             { label: 'Messagerie / colis quotidiens' },
  'Messagerie express':   { label: 'Messagerie express (délais courts)' },
  Colis:                  { label: 'Colis / e-commerce' },
  Distribution:           { label: 'Distribution multi-points' },
  National:               { label: 'Lignes nationales régulières' },
  Palettes:               { label: 'Palettes (GMS, industrie)' },
  Vrac:                   { label: 'Vrac / matières premières' },
  Chantier:               { label: 'Approvisionnement chantier' },
  EU:                     { label: 'Liaisons intra-UE' },
  'VTC léger':            { label: 'Courses urbaines légères / urgences' },
  'Économique':           { label: 'Exploitation économique (coût/km réduit)' },
  BTP:                    { label: 'BTP / matériaux de chantier' },
  'Vrac lourd':           { label: 'Vrac & gros tonnage industriel' },
  'Export UE':            { label: 'Contrats internationaux UE' },
  'Longue distance':      { label: 'Trajets longue distance (>500 km)' },
  // ── Nouveaux libellés normalisés (Phase F3) ─────────────────────────────
  'Messagerie/Colis':         { label: 'Messagerie / colis (urbain, e-commerce, express)' },
  'Frigo/Pharma':             { label: 'Frigo (GMS, restauration, pharma, agroalim. premium)' },
  'BTP/Matériaux/Engins TP':  {
    label: 'BTP / matériaux / engins TP (chantier, vrac lourd, benne)',
    blocks: ['Export UE prestige', 'ZFE / urbain dense'],
  },
  'ADR/Produits sensibles':   { label: 'ADR / matières dangereuses / produits sensibles' },
  'Vrac/Palettes':            { label: 'Vrac / palettes / distribution nationale' },
  // 05/02/2026 — Q1 retour user prod : transport exceptionnel
  'Convoi exceptionnel':      {
    label: 'Convoi exceptionnel (éoliennes, fusée, maison préfab — récompenses 10-25K €/mission)',
    blocks: ['Urbain dense', 'Distribution standard', 'Frigo', 'ADR / chimie'],
  },
  // ── Spécialités physiques (héritées) ────────────────────────────────────
  // 05/02/2026 — Q1 retour user prod : « Transport animaux » (bétaillère)
  // supprimé du catalogue.
  'BTP lourd':            {
    label: 'BTP lourd (engins de chantier)',
    blocks: ['Export UE prestige', 'ZFE / urbain dense', 'Image RSE / appels publics'],
  },
  'Engins de chantier':   {
    label: 'Porte-engins TP (pelles, bulldozers, mini-pelles)',
    blocks: ['Export UE prestige', 'ZFE / urbain dense'],
  },
  Prestige:               {
    label: 'Contrats prestige (image entreprise, hauts revenus)',
    blocks: ['ZFE / urbain dense', 'Image RSE / appels publics', 'BTP / engins de chantier'],
  },
  ZFE:                    {
    label: 'ZFE illimité (urbain dense, électrique)',
    blocks: ['BTP lourd / engins de chantier', 'Longue distance > 500 km/j'],
  },
  'Image verte':          {
    label: 'Marchés publics & appels d\'offres RSE',
    blocks: ['BTP lourd / engins de chantier'],
  },
};

/**
 * Returns a structured pedagogical description for the BuyTab UI.
 * @param {Object} vehicle — vehicle entry from VEHICLES.
 * @returns {{ label:string, license:string, accessible:string[], blocked:string[], hint:string }}
 */
export function describeVehicleAccessibility(vehicle) {
  const profile = TIER_PROFILE[vehicle.tier] || TIER_PROFILE[1];
  const specs = vehicle.specialties || [];

  // Resolve each specialty into its accessible label + extra blocks.
  const specMeta = specs
    .map(s => SPECIALTY_HINTS[s])
    .filter(Boolean);
  const specLabels = specMeta.map(m => m.label);
  const specBlocks = specMeta.flatMap(m => m.blocks || []);

  // ── Phase F3 (04/02/2026) — Pédagogie implicite par modèle ────────────
  // Les specialties[] sont désormais normalisées sur 8 axes ; mais l'UI
  // doit toujours présenter les nuances métier (Prestige, Porte-engins,
  // ZFE/RSE…). On enrichit ici, en fonction de l'id et du type moteur.
  const implicitLabels = [];
  const implicitBlocks = [];
  if (vehicle.id === 'scanex-r650-v8-prestige') {
    implicitLabels.push(SPECIALTY_HINTS.Prestige.label, SPECIALTY_HINTS['Longue distance'].label);
    implicitBlocks.push(...SPECIALTY_HINTS.Prestige.blocks);
  }
  if (vehicle.id === 'dafora-xf-porte-engins') {
    implicitLabels.push(SPECIALTY_HINTS['BTP lourd'].label, SPECIALTY_HINTS['Engins de chantier'].label);
    implicitBlocks.push(...SPECIALTY_HINTS['BTP lourd'].blocks);
  }
  if (vehicle.engineType === 'electric') {
    implicitLabels.push(SPECIALTY_HINTS['Image verte'].label);
    implicitBlocks.push(...SPECIALTY_HINTS.ZFE.blocks);
    implicitBlocks.push(...SPECIALTY_HINTS['Image verte'].blocks);
  }
  // Longue distance implicite pour véhicules Tier 3+ avec Export UE
  if (vehicle.tier >= 3 && specs.includes('Export UE')) {
    implicitLabels.push(SPECIALTY_HINTS['Longue distance'].label);
  }

  // Specialty-driven tiers (4, 5) : the accessible list IS the specialty
  // list (no generic tier bucket). Other tiers : merge as before.
  const accessible = profile.specialtyDriven
    ? Array.from(new Set([...specLabels, ...implicitLabels]))
    : Array.from(new Set([...(profile.accessible || []), ...specLabels, ...implicitLabels]));

  // Blocked list always includes the tier baseline + extra blocks from
  // specialties (electric truck blocks BTP, V8 prestige blocks RSE, etc.)
  const blocked = Array.from(new Set([...(profile.blocked || []), ...specBlocks, ...implicitBlocks]));

  return {
    label: profile.label,
    license: vehicle.license || 'B',
    accessible,
    blocked,
    hint: profile.hint,
  };
}

export const VEHICLE_LICENSE_LABELS = {
  B:  { code: 'Permis B',  full: '< 3,5 T — tous publics',          color: '#10B981' },
  C:  { code: 'Permis C',  full: '3,5 T à 26 T — formation requise', color: '#0EA5E9' },
  CE: { code: 'Permis CE', full: 'Semi-remorque 44 T — formation lourde', color: '#7C3AED' },
};

// 05/02/2026 — Q1 retour user prod : helper défensif pour éviter les écrans
// blancs si un nouveau code de license non répertorié apparaît (ex. ancien
// 'CE+exceptionnel' qui crashait BuyTab/LeaseTab). On retombe sur le label
// du permis le plus restrictif (CE) avec un libellé de fallback explicite.
export function getLicenseLabel(licenseCode) {
  const k = licenseCode || 'B';
  if (VEHICLE_LICENSE_LABELS[k]) return VEHICLE_LICENSE_LABELS[k];
  return { code: `Permis ${k}`, full: 'Permis spécial requis', color: '#7C3AED' };
}
